import { useDataModel } from '@thd-nucleus/data-sources';
import { DRAWER_FLOWS } from '../../../constants/drawerFlows';
import { useOutOfStockRecommendations } from './useOutOfStockRecommendations';
import { useFitOptionsProducts } from './useFitOptionsProducts';

/**
 * Picks the product source for the swap drawer based on the flow. Only the active flow's
 * queries run; the others are skipped.
 * - default: packageSwapRecs.
 * - fitOptions: searchModel by navParam, scoped and filtered to userDimensions when provided
 *   (useFitOptionsProducts).
 * - oosRecommendations: more-like-this recs filtered to deliverable items (useOutOfStockRecommendations).
 */
export const useSwapProducts = ({
  flow,
  itemId,
  storeId,
  zipCode,
  channel,
  promoId,
  filterResponse,
  navParam,
  userDimensions,
  fetch
}) => {
  const isFitOptionsFlow = flow === DRAWER_FLOWS.FIT_OPTIONS;
  const isOutOfStockFlow = flow === DRAWER_FLOWS.OOS_RECOMMENDATIONS;
  const isRecsFlow = !isFitOptionsFlow && !isOutOfStockFlow;

  const recs = useDataModel('packageSwapRecs', {
    variables: {
      itemId,
      storeId,
      promoId,
      filterResponse
    },
    ssr: false,
    skip: !isRecsFlow || !itemId || !fetch
  });

  const fitOptions = useFitOptionsProducts({
    itemId,
    storeId,
    navParam,
    userDimensions,
    fetch,
    skip: !isFitOptionsFlow
  });

  const outOfStock = useOutOfStockRecommendations({
    itemId,
    storeId,
    zipCode,
    channel,
    skip: !isOutOfStockFlow || !itemId || !fetch
  });

  if (isOutOfStockFlow) {
    return {
      // The drawer opens immediately and shows a skeleton while the recs chain resolves
      hasResponse: fetch,
      products: outOfStock.products,
      loading: outOfStock.loading,
      error: undefined,
      currentItemSpecificationGroup: outOfStock.currentItemSpecificationGroup
    };
  }

  if (isFitOptionsFlow) {
    return { ...fitOptions, currentItemSpecificationGroup: null };
  }

  return {
    hasResponse: Boolean(recs.data),
    products: recs.data?.packageSwapRecs?.products,
    loading: recs.loading,
    error: recs.error,
    currentItemSpecificationGroup: null
  };
};
